import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Paper, Typography } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { isNonAvailableWeekday } from "../../../utils/dateUtils";
import { isWithinInterval } from "date-fns";
import AdminInfoContent from "./AdminInfoContent";
import BookingDetailsView from "./BookingDetailsView";
import DateAvailabilityView from "./DateAvailabilityView";
import WeekdaySettingsView from "./WeekdaySettingsView";

const AdminDetailsPanel = ({
  showDateDetails,
  selectedDateStr,
  bookedDateRanges, // Updated from bookedDates
  unavailableDates,
  bookingDetails,
  setBookingDetails,
  setBookedDateRanges, // Updated from setBookedDates
  detailsRef,
  availableWeekdays,
  setAvailableWeekdays,
  setUnavailableDates,
  setGlobalConfirmAction,
  handleCloseDetails,
  setSnackbar,
}) => {
  // Helper function to check if a date is within any booking range
  const isDateInBookingRange = useCallback(
    (dateStr) => {
      if (!dateStr || !bookedDateRanges.length) return false;

      const dateObj = new Date(dateStr);
      return bookedDateRanges.some((range) => {
        if (!range.startDate || !range.endDate) return false;
        return isWithinInterval(dateObj, {
          start: new Date(range.startDate),
          end: new Date(range.endDate),
        });
      });
    },
    [bookedDateRanges]
  );

  // Get date status internally
  const getDateStatus = useCallback(
    (dateStr) => {
      if (isDateInBookingRange(dateStr)) return "booked";
      if (unavailableDates.includes(dateStr)) return "unavailable";
      return "available";
    },
    [isDateInBookingRange, unavailableDates]
  );

  // Render appropriate content based on the selected date
  const renderDetailContent = () => {
    if (selectedDateStr === "weekday-settings") {
      return (
        <WeekdaySettingsView
          availableWeekdays={availableWeekdays}
          setAvailableWeekdays={setAvailableWeekdays}
          handleCloseDetails={handleCloseDetails}
          setSnackbar={setSnackbar}
        />
      );
    }

    // Determine what to show based on date status
    const dateStatus = getDateStatus(selectedDateStr);

    // If selected date is booked and we have booking details
    if (dateStatus === "booked" && bookingDetails) {
      return (
        <>
          <Typography
            variant="h5"
            sx={{
              color: "#c84653",
              mb: 3,
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
            }}
          >
            <CalendarMonth sx={{ mr: 1 }} />
            Date de réservation
          </Typography>

          <BookingDetailsView
            bookingDetails={bookingDetails}
            selectedDateStr={selectedDateStr}
            setBookingDetails={setBookingDetails}
            setBookedDateRanges={setBookedDateRanges} // Updated prop name
            handleCloseDetails={handleCloseDetails}
            setGlobalConfirmAction={setGlobalConfirmAction}
            setSnackbar={setSnackbar}
          />
        </>
      );
    }

    // If selected date falls on a non-available weekday
    if (isNonAvailableWeekday(selectedDateStr, availableWeekdays)) {
      return (
        <WeekdaySettingsView
          availableWeekdays={availableWeekdays}
          setAvailableWeekdays={setAvailableWeekdays}
          handleCloseDetails={handleCloseDetails}
          setSnackbar={setSnackbar}
        />
      );
    }

    // Default case: available or unavailable date
    return (
      <>
        <Typography
          variant="h5"
          sx={{
            color: "#c84653",
            mb: 3,
            display: "flex",
            alignItems: "center",
            fontWeight: 500,
          }}
        >
          <CalendarMonth sx={{ mr: 1 }} />
          Gestion de la disponibilité
        </Typography>

        <DateAvailabilityView
          dateStatus={dateStatus}
          selectedDateStr={selectedDateStr}
          unavailableDates={unavailableDates}
          setUnavailableDates={setUnavailableDates}
          handleCloseDetails={handleCloseDetails}
          setSnackbar={setSnackbar}
        />
      </>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        height: "100%",
        minHeight: { xs: "500px", md: "600px" },
        p: { xs: 0.5, sm: 1, md: 3 },
        borderRadius: "15px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div className="animation-container" style={{ height: "100%" }}>
        <AnimatePresence mode="wait" initial={false}>
          {!showDateDetails ? (
            <motion.div
              key="info-content"
              initial={{ x: showDateDetails ? 300 : -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{ height: "100%" }}
            >
              <AdminInfoContent />
            </motion.div>
          ) : (
            <motion.div
              key="date-details"
              ref={detailsRef}
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              {renderDetailContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Paper>
  );
};

export default AdminDetailsPanel;
